import { useState, type FC } from "react";
import InvoiceListOverviewItem from "../../molecules/invoices/InvoiceListOverviewItem";
import {
  InvoiceAssessmentTags,
  InvoiceValidationTags,
  type InvoiceTag,
} from "../../../models/InvoiceTags";
import Card from "../../atoms/Card";
import FilterChip from "../../atoms/inputs/FilterChip";
import {
  getInvoiceSubstatusFilter,
  getInvoiceSubstatusVariables,
  getInvoiceTagTitleKey,
  type InvoiceSubstatus,
} from "../../../utils/invoicesHelpers";
import { Row } from "react-grid-system";
import { HorizontalSpacer } from "../../atoms/Spacers";
import InvoiceListFilterRow from "../../molecules/invoices/InvoiceListFilterRow";
import TabBar from "../../molecules/TabBar";
import type { InvoiceProcessStatus } from "../../../models/InvoiceStatusModel";
import { useTranslation } from "react-i18next";
import { theme } from "../../../theme";
import SlideActionToolbar from "../../molecules/SlideActionToolbar";
import {
  TagSlidePanel,
  NumberOfSelectedContainer,
} from "../../molecules/invoices/InvoiceToolbarPanels";
import { HighligtActionButton } from "../../atoms/SlideActionPanel";
import BodySmall from "../../atoms/typography/BodySmall";
import { AddIcon } from "../../atoms/icons/AddIcon";
import { CrossIcon } from "../../atoms/icons/CrossIcon";
import { useInvoiceList } from "../../molecules/invoices/useInvoiceList";

interface Props {
  clientIdentifier: string;
  displayAsRecievedInvoices?: boolean;
}

const tabIdToStatuses = (tabId: string): Array<InvoiceProcessStatus> => {
  if (tabId === "underAssessment") return ["ASSESSMENT"];
  if (tabId === "underValidation") return ["VALIDATION"];
  if (tabId === "rejected") return ["REJECTED"];
  if (tabId === "done") return ["DONE"];
  if (tabId === "underRegistration") return ["REGISTRATION"];
  return ["ASSESSMENT", "VALIDATION", "REJECTED", "DONE", "REGISTRATION"];
};

const statusTabs = [
  { id: "all", name: "invoices.all" },
  { id: "underRegistration", name: "invoices.underRegistration" },
  { id: "underValidation", name: "invoices.underValidation" },

  { id: "underAssessment", name: "invoices.underAssessment" },
  { id: "rejected", name: "invoices.rejected" },
  { id: "done", name: "invoices.done" },
];

export type StatusTabId =
  | "all"
  | "underAssessment"
  | "underValidation"
  | "rejected"
  | "done"
  | "autopilotPause";

const ClientInvoiceListPage: FC<Props> = ({
  clientIdentifier,
  displayAsRecievedInvoices,
}) => {
  const { t } = useTranslation();
  const [selectedStatusTab, setSelectedStatusTab] =
    useState<StatusTabId>("all");
  const [selectedStatuses, setSelectedStatuses] = useState<
    Array<InvoiceProcessStatus> | undefined
  >(["ASSESSMENT", "VALIDATION", "REJECTED", "DONE", "REGISTRATION"]);
  const [selectedSubstatuses, setSelectedSubstatuses] = useState<
    InvoiceSubstatus[]
  >([]);

  const {
    selectedInvoices,
    setSelectedInvoices,
    invoiceDateFrom,
    invoiceDateTo,
    dueDateFrom,
    dueDateTo,
    selectedAmounts,
    selectedTag,
    filterResetKey,
    openRejectModal,
    rejectModal,
    onInvoicesLoaded,
    getTagInitialState,
    onApplyTags,
    clearTag,
    onToggleTag,
    onSelectDates,
    onSelectAmount,
    onClearFilters,
  } = useInvoiceList({
    onClearExtra: () => {
      setSelectedStatuses(tabIdToStatuses(selectedStatusTab));
      setSelectedSubstatuses([]);
    },
  });

  const visibleTags =
    selectedStatusTab === "underAssessment"
      ? InvoiceAssessmentTags
      : selectedStatusTab === "underValidation"
        ? InvoiceValidationTags
        : selectedStatusTab === "all"
          ? [...InvoiceAssessmentTags, ...InvoiceValidationTags]
          : [];

  const onSelectStatusTab = (tabId: string) => {
    setSelectedStatusTab(tabId as StatusTabId);
    setSelectedInvoices([]);

    const newVisibleTags =
      tabId === "underAssessment"
        ? InvoiceAssessmentTags
        : tabId === "underValidation"
          ? InvoiceValidationTags
          : tabId === "all"
            ? [...InvoiceAssessmentTags, ...InvoiceValidationTags]
            : [];
    if (selectedTag && !newVisibleTags.includes(selectedTag as InvoiceTag)) {
      clearTag();
    }

    setSelectedStatuses(tabIdToStatuses(tabId));
    setSelectedSubstatuses([]);
  };

  const { validationStatuses, assessmentStatuses } =
    getInvoiceSubstatusVariables(selectedStatusTab, selectedSubstatuses);

  return (
    <div>
      <TabBar
        tabs={statusTabs}
        selectedTabId={selectedStatusTab}
        onSelectTab={onSelectStatusTab}
      />

      <HorizontalSpacer spacing={1.5} />

      <Row nogutter justify="between" align="center">
        <div style={{ gap: 16, display: "flex", alignItems: "center" }}>
          {visibleTags.map((tag) => (
            <FilterChip
              key={tag}
              titleKey={getInvoiceTagTitleKey(tag)}
              selected={selectedTag === tag}
              onSelect={() => onToggleTag(tag)}
            />
          ))}
        </div>
      </Row>

      <HorizontalSpacer spacing={1} />

      <InvoiceListFilterRow
        invoiceDateFrom={invoiceDateFrom}
        invoiceDateTo={invoiceDateTo}
        dueDateFrom={dueDateFrom}
        dueDateTo={dueDateTo}
        onSelectDates={onSelectDates}
        onSelectAmount={onSelectAmount}
        selectedAmounts={selectedAmounts ?? undefined}
        onClearFilters={onClearFilters}
        filterResetKey={filterResetKey.toString()}
        statusFilter={getInvoiceSubstatusFilter(selectedStatusTab)}
        selectedStatuses={selectedSubstatuses}
        onSelectStatuses={(statuses) => {
          setSelectedInvoices([]);
          setSelectedSubstatuses(statuses as InvoiceSubstatus[]);
        }}
      />

      <HorizontalSpacer spacing={1.5} />

      <Row nogutter justify="between" align="end">
        <div>
          {selectedStatusTab !== "rejected" && selectedStatusTab !== "done" && (
            <SlideActionToolbar
              minWidth={240}
              bgColor={theme.colors.white}
              actions={(openPanel, closePanel) => (
                <>
                  <NumberOfSelectedContainer>
                    <BodySmall color={theme.colors.textSecondary}>
                      {selectedInvoices.length}{" "}
                      {selectedInvoices.length === 1
                        ? t("common.selectedSingular").toLowerCase()
                        : t("common.selected").toLowerCase()}
                    </BodySmall>
                  </NumberOfSelectedContainer>
                  <HighligtActionButton
                    onClick={() => openPanel("tag")}
                    disabled={
                      selectedInvoices.length === 0 ||
                      selectedStatusTab === "all"
                    }
                  >
                    <AddIcon />
                    <BodySmall>{t("invoices.tag")}</BodySmall>
                  </HighligtActionButton>
                  <HighligtActionButton
                    onClick={() => {
                      closePanel();
                      openRejectModal();
                    }}
                    disabled={selectedInvoices.length === 0}
                  >
                    <CrossIcon />
                    <BodySmall>{t("documentHandler.reject")}</BodySmall>
                  </HighligtActionButton>
                </>
              )}
              panels={(isActivePanel, panelRef, panelDirection, closePanel) => (
                <>
                  {isActivePanel("tag") && (
                    <TagSlidePanel
                      key={`tag-${panelDirection}`}
                      panelRef={panelRef}
                      panelDirection={panelDirection}
                      closePanel={closePanel}
                      selectedCount={selectedInvoices.length}
                      tags={
                        selectedStatusTab === "underAssessment"
                          ? InvoiceAssessmentTags.map((tag) => ({
                              key: tag,
                              labelKey: getInvoiceTagTitleKey(tag),
                              selected: getTagInitialState(tag),
                            }))
                          : selectedStatusTab === "underValidation"
                            ? InvoiceValidationTags.map((tag) => ({
                                key: tag,
                                labelKey: getInvoiceTagTitleKey(tag),
                                selected: getTagInitialState(tag),
                              }))
                            : []
                      }
                      onApply={onApplyTags}
                    />
                  )}
                </>
              )}
            />
          )}
          {rejectModal}
        </div>
      </Row>

      <HorizontalSpacer spacing={1.5} />

      <Card spacing={0} border>
        <InvoiceListOverviewItem
          selectedInvoices={selectedInvoices}
          setSelectedInvoices={setSelectedInvoices}
          clientIdentifier={clientIdentifier}
          invoiceDateFrom={invoiceDateFrom}
          invoiceDateTo={invoiceDateTo}
          dueDateFrom={dueDateFrom}
          dueDateTo={dueDateTo}
          invoiceStatuses={selectedStatuses}
          validationStatuses={validationStatuses}
          assessmentStatuses={assessmentStatuses}
          amountFrom={selectedAmounts?.min}
          amountTo={selectedAmounts?.max}
          selectedTags={selectedTag ? [selectedTag] : undefined}
          onInvoicesLoaded={onInvoicesLoaded}
          clientFilterType={displayAsRecievedInvoices ? "RECIPIENT" : undefined}
          selectedTab={selectedStatusTab}
        />
      </Card>
    </div>
  );
};

export default ClientInvoiceListPage;
